import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ICustomPushNotificationType } from '../../types/customPush';
import { customPushService } from '../../services/customPushService';

interface NotificationTypeDialogProps {
  open: boolean;
  onClose: () => void;
  notificationType: ICustomPushNotificationType | null;
  onSaved: () => void;
}

const NotificationTypeDialog: React.FC<NotificationTypeDialogProps> = ({
  open,
  onClose,
  notificationType,
  onSaved,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isEdit = !!notificationType;

  const [typeValue, setTypeValue] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setTypeValue(notificationType?.notificationType || '');
      setDescription(notificationType?.description || '');
      setErrors({});
    }
  }, [open, notificationType]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!typeValue.trim()) newErrors.typeValue = 'Notification type is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      if (isEdit) {
        await customPushService.updateNotificationType(typeValue.trim(), description.trim());
      } else {
        await customPushService.createNotificationType(typeValue.trim(), description.trim());
      }
      onSaved();
      onClose();
    } catch (error: any) {
      setErrors({ typeValue: typeof error === 'string' ? error : 'Failed to save notification type' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Notification Type' : 'New Notification Type'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Notification Type"
            value={typeValue}
            onChange={(e) => setTypeValue(e.target.value)}
            error={!!errors.typeValue}
            helperText={errors.typeValue}
            disabled={isEdit}
            required
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} fullWidth={isMobile}>
          Close
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={loading} fullWidth={isMobile}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationTypeDialog;
